import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Minus, Target, AlertTriangle, CheckCircle, Calendar, Activity } from 'lucide-react';
import { api } from '@/utils/api';
import { useLanguage } from '@/LanguageContext';

const ProgressChart = ({ childId }) => {
  const { t } = useLanguage();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProgress();
  }, [childId]);

  const fetchProgress = async () => {
    try {
      const res = await api.getChildProgress(childId);
      setProgress(res.data);
    } catch (err) {
      console.error('Error fetching progress:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'improving': return <TrendingUp className="text-green-600" size={20} />;
      case 'declining': return <TrendingDown className="text-red-600" size={20} />;
      default: return <Minus className="text-gray-600" size={20} />;
    }
  };

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'improving': return 'text-green-600 bg-green-50';
      case 'declining': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'high': return 'bg-red-500';
      case 'moderate': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-400';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'border-red-400 bg-red-50';
      case 'medium': return 'border-yellow-400 bg-yellow-50';
      default: return 'border-green-400 bg-green-50';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-[#0F4C81] font-bold">{t('common.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        {t('progress.errorLoading')}
      </div>
    );
  }

  if (!progress || progress.total_screenings === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-2xl border-2 border-slate-100">
        <Activity className="mx-auto text-[#9CA3AF] mb-4" size={48} />
        <p className="text-[#4B5563] mb-2">{t('progress.noProgressData')}</p>
        <p className="text-sm text-[#9CA3AF]">{t('progress.completeScreeningFirst')}</p>
      </div>
    );
  }

  const { progress_records, trend_analysis, recommendations } = progress;

  return (
    <div className="space-y-6" data-testid="progress-chart">
      {/* Overall Progress Summary */}
      {trend_analysis && (
        <div className="bg-gradient-to-br from-[#0F4C81] to-[#1e5a9a] rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold flex items-center space-x-2">
              <TrendingUp size={24} />
              <span>{t('progress.overallTrend')}</span>
            </h3>
            <div className={`flex items-center space-x-2 px-4 py-2 rounded-full ${
              trend_analysis.overall_trend === 'improving' ? 'bg-green-500' :
              trend_analysis.overall_trend === 'declining' ? 'bg-red-500' : 'bg-gray-500'
            }`}>
              {getTrendIcon(trend_analysis.overall_trend)}
              <span className="font-bold capitalize">{trend_analysis.overall_trend}</span>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white/10 rounded-xl p-4">
              <p className="text-white/70 text-sm">{t('progress.totalAssessments')}</p>
              <p className="text-3xl font-bold">{trend_analysis.total_assessments}</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4">
              <p className="text-white/70 text-sm">{t('progress.scoreChange')}</p>
              <p className={`text-3xl font-bold ${trend_analysis.score_change >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                {trend_analysis.score_change >= 0 ? '+' : ''}{trend_analysis.score_change}%
              </p>
            </div>
            <div className="bg-white/10 rounded-xl p-4 col-span-2">
              <p className="text-white/70 text-sm">{t('progress.trackingPeriod')}</p>
              <p className="text-lg font-semibold">
                {new Date(trend_analysis.first_assessment_date).toLocaleDateString()} - {new Date(trend_analysis.latest_assessment_date).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Progress Timeline */}
      <div className="bg-white rounded-2xl border-2 border-slate-100 p-6">
        <h3 className="text-xl font-bold text-[#111827] mb-4 flex items-center space-x-2">
          <Calendar size={20} className="text-[#0F4C81]" />
          <span>{t('progress.progressTimeline')}</span>
        </h3>
        
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200" />
          
          <div className="space-y-4">
            {progress_records.map((record, idx) => (
              <div key={record.screening_id} className="relative pl-10" data-testid={`progress-record-${idx}`}>
                {/* Timeline dot */}
                <div className={`absolute left-2 w-5 h-5 rounded-full border-4 border-white ${getRiskColor(record.risk_level)}`} />
                
                <div className="bg-[#F9FAFB] rounded-xl p-4 hover:bg-[#F3F4F6] transition-colors">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <div className="flex items-center space-x-3">
                      <span className="text-sm font-semibold text-[#4B5563]">
                        {new Date(record.date).toLocaleDateString()}
                      </span>
                      <span className="text-xs bg-[#E5E7EB] px-2 py-1 rounded-full">
                        {record.age_months} {t('screening.months')}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`text-xs px-3 py-1 rounded-full font-bold ${
                        record.risk_level === 'high' ? 'bg-red-100 text-red-800' :
                        record.risk_level === 'moderate' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {record.risk_level?.toUpperCase()}
                      </span>
                      <span className="text-sm font-bold text-[#0F4C81]">
                        {record.overall_score}%
                      </span>
                    </div>
                  </div>
                  
                  {/* Domain scores mini chart */}
                  {record.domain_scores && Object.keys(record.domain_scores).length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {Object.entries(record.domain_scores).map(([domain, score]) => (
                        <div key={domain} className="flex items-center space-x-1 text-xs">
                          <span className="text-[#6B7280]">{domain.replace('_development', '').replace('_', ' ')}:</span>
                          <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${
                                score >= 70 ? 'bg-green-500' : score >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${score}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Domain Trends */}
      {trend_analysis?.domain_trends && Object.keys(trend_analysis.domain_trends).length > 0 && (
        <div className="bg-white rounded-2xl border-2 border-slate-100 p-6">
          <h3 className="text-xl font-bold text-[#111827] mb-4 flex items-center space-x-2">
            <Target size={20} className="text-[#0F4C81]" />
            <span>{t('progress.domainTrends')}</span>
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(trend_analysis.domain_trends).map(([domain, data]) => (
              <div 
                key={domain}
                className={`rounded-xl p-4 border-2 ${getTrendColor(data.trend)}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold capitalize">{domain.replace('_', ' ')}</span>
                  {getTrendIcon(data.trend)}
                </div>
                <div className="flex items-center space-x-4">
                  <div>
                    <span className="text-xs text-gray-500">{t('progress.first')}</span>
                    <p className="font-bold">{data.first}%</p>
                  </div>
                  <div className="flex-1 h-1 bg-gray-200 rounded">
                    <div 
                      className={`h-full rounded transition-all ${
                        data.change >= 0 ? 'bg-green-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${Math.abs(data.change)}%` }}
                    />
                  </div>
                  <div>
                    <span className="text-xs text-gray-500">{t('progress.latest')}</span>
                    <p className="font-bold">{data.latest}%</p>
                  </div>
                </div>
                <p className={`text-sm mt-2 ${data.change >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                  {data.change >= 0 ? '+' : ''}{data.change}% {t('progress.change')}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Dynamic Recommendations */}
      {recommendations && recommendations.length > 0 && (
        <div className="bg-white rounded-2xl border-2 border-slate-100 p-6">
          <h3 className="text-xl font-bold text-[#111827] mb-4 flex items-center space-x-2">
            <CheckCircle size={20} className="text-[#0F4C81]" />
            <span>{t('progress.dynamicRecommendations')}</span>
          </h3>
          
          <div className="space-y-4">
            {recommendations.map((rec, idx) => (
              <div 
                key={idx}
                className={`rounded-xl p-4 border-2 ${getPriorityColor(rec.priority)}`}
                data-testid={`recommendation-${idx}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    {rec.priority === 'high' ? <AlertTriangle className="text-red-600" size={18} /> : 
                     rec.priority === 'medium' ? <Target className="text-yellow-600" size={18} /> :
                     <CheckCircle className="text-green-600" size={18} />}
                    <h4 className="font-bold text-[#111827]">{rec.title}</h4>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-bold ${
                    rec.priority === 'high' ? 'bg-red-200 text-red-800' :
                    rec.priority === 'medium' ? 'bg-yellow-200 text-yellow-800' :
                    'bg-green-200 text-green-800'
                  }`}>
                    {rec.priority?.toUpperCase()}
                  </span>
                </div>
                <p className="text-sm text-[#4B5563] mb-3">{rec.description}</p>
                {rec.action_items && rec.action_items.length > 0 && (
                  <div className="bg-white/50 rounded-lg p-3">
                    <span className="text-xs font-semibold text-[#4B5563] uppercase mb-2 block">{t('progress.actionItems')}</span>
                    <ul className="space-y-1">
                      {rec.action_items.map((item, i) => (
                        <li key={i} className="text-sm text-[#374151] flex items-center space-x-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#0F4C81]" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgressChart;
